import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, Users, Crown, Activity, Trophy, DollarSign, Plus, Trash2 } from "lucide-react";
import { Wordmark } from "../components/Brand.jsx";
import api from "../lib/api.js";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [newCh, setNewCh] = useState({
    title: "", description: "", type: "weekly",
    target_steps: 0, target_sessions: 0, points_reward: 200, duration_days: 7,
  });

  const load = async () => {
    const [s, u, c] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/users"), api.get("/challenges"),
    ]);
    setStats(s.data); setUsers(u.data); setChallenges(c.data);
  };
  useEffect(() => { load(); }, []);

  const createChallenge = async () => {
    if (!newCh.title) return toast.error("Title required");
    await api.post("/admin/challenges", newCh);
    setNewCh({ ...newCh, title: "", description: "" });
    toast.success("Challenge created");
    load();
  };
  const deleteChallenge = async (id) => {
    await api.delete(`/admin/challenges/${id}`);
    toast.success("Deleted"); load();
  };

  if (!stats) return <div className="text-slate-400 p-12">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#0a1822] text-white">
      <header className="border-b border-[#1a2f40] px-6 lg:px-10 py-4 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Link to="/home" className="text-slate-400 inline-flex items-center gap-1 text-sm" data-testid="admin-back"><ArrowLeft size={16} /> App</Link>
          <Wordmark />
        </div>
        <div className="flex items-center gap-2 text-sm text-[#2fbf71] font-semibold">
          <ShieldCheck size={16} /> Admin Console
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <h1 className="ps-heading text-4xl font-bold">Operations</h1>
        <p className="text-slate-400 mt-2">Platform-wide metrics, content and challenge management.</p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          <Stat label="Total Users" v={stats.total_users} icon={Users} c="#2fbf71" tid="admin-stat-users" />
          <Stat label="Premium" v={stats.premium_users} icon={Crown} c="#F59E0B" tid="admin-stat-premium" />
          <Stat label="Walking Sessions" v={stats.walking_sessions} icon={Activity} c="#00B4D8" tid="admin-stat-walks" />
          <Stat label="Posture Sessions" v={stats.posture_sessions} icon={Trophy} c="#a78bfa" tid="admin-stat-posture" />
          <Stat label="Revenue" v={`$${stats.revenue_usd}`} icon={DollarSign} c="#2fbf71" tid="admin-stat-revenue" />
          <Stat label="Challenges" v={stats.challenges} icon={Trophy} c="#F59E0B" tid="admin-stat-challenges" />
          <Stat label="Products" v={stats.products} icon={Activity} c="#00B4D8" tid="admin-stat-products" />
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mt-10">
          <div className="ps-card p-6">
            <div className="ps-heading font-bold text-xl">Create Challenge</div>
            <div className="mt-4 space-y-3">
              <input
                data-testid="admin-ch-title"
                placeholder="Title" value={newCh.title} onChange={(e) => setNewCh({ ...newCh, title: e.target.value })}
                className="w-full bg-[#142534] border border-[#1a2f40] rounded-xl px-4 py-2.5 outline-none focus:border-[#2fbf71]"
              />
              <textarea
                data-testid="admin-ch-desc"
                placeholder="Description" value={newCh.description} onChange={(e) => setNewCh({ ...newCh, description: e.target.value })}
                rows={2}
                className="w-full bg-[#142534] border border-[#1a2f40] rounded-xl px-4 py-2.5 outline-none focus:border-[#2fbf71]"
              />
              <div className="grid grid-cols-2 gap-3">
                <select
                  data-testid="admin-ch-type"
                  value={newCh.type} onChange={(e) => setNewCh({ ...newCh, type: e.target.value })}
                  className="bg-[#142534] border border-[#1a2f40] rounded-xl px-4 py-2.5"
                >
                  <option value="daily">Daily</option><option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option><option value="corporate">Corporate</option>
                </select>
                <input
                  type="number" placeholder="Target steps" value={newCh.target_steps}
                  onChange={(e) => setNewCh({ ...newCh, target_steps: +e.target.value })}
                  className="bg-[#142534] border border-[#1a2f40] rounded-xl px-4 py-2.5"
                />
                <input
                  type="number" placeholder="Points reward" value={newCh.points_reward}
                  onChange={(e) => setNewCh({ ...newCh, points_reward: +e.target.value })}
                  className="bg-[#142534] border border-[#1a2f40] rounded-xl px-4 py-2.5"
                />
                <input
                  type="number" placeholder="Duration (days)" value={newCh.duration_days}
                  onChange={(e) => setNewCh({ ...newCh, duration_days: +e.target.value })}
                  className="bg-[#142534] border border-[#1a2f40] rounded-xl px-4 py-2.5"
                />
              </div>
              <button data-testid="admin-ch-create" onClick={createChallenge} className="ps-button-primary inline-flex items-center gap-2"><Plus size={16} /> Create</button>
            </div>
          </div>

          <div className="ps-card p-6">
            <div className="ps-heading font-bold text-xl">All Challenges</div>
            <div className="mt-4 space-y-2 max-h-[400px] overflow-y-auto">
              {challenges.map((c) => (
                <div key={c.id} className="ps-card-elev p-3 flex items-center gap-3">
                  <div className="flex-1">
                    <div className="font-semibold text-sm">{c.title}</div>
                    <div className="text-xs text-slate-400">{c.type} · {c.points_reward} pts · {c.participant_count} members</div>
                  </div>
                  <button data-testid={`admin-del-${c.id}`} onClick={() => deleteChallenge(c.id)} className="p-2 text-[#EF4444] hover:bg-[#142534] rounded-lg">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-10 ps-card p-6">
          <div className="ps-heading font-bold text-xl">Users</div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-slate-400 border-b border-[#1a2f40]">
                <tr>
                  <th className="py-2 px-2">Name</th><th>Email</th><th>Role</th><th>Premium</th><th>Streak</th><th>Points</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-[#142534]">
                    <td className="py-2 px-2 font-semibold">{u.name}</td>
                    <td className="text-slate-400">{u.email}</td>
                    <td><span className="text-xs px-2 py-0.5 rounded-full bg-[#142534] border border-[#1a2f40]">{u.role}</span></td>
                    <td>{u.is_premium ? <Crown size={14} color="#F59E0B" /> : "—"}</td>
                    <td>{u.streak || 0}</td>
                    <td className="text-[#2fbf71] font-semibold">{u.points || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}

function Stat({ label, v, icon: Icon, c, tid }) {
  return (
    <div className="ps-card p-5" data-testid={tid}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-2xl bg-[#142534] flex items-center justify-center"><Icon size={18} color={c} /></div>
        <div className="text-xs uppercase tracking-widest text-slate-400 font-bold">{label}</div>
      </div>
      <div className="ps-heading text-3xl font-bold mt-3">{v}</div>
    </div>
  );
}
